import React, { useEffect, useState } from "react";
import { Mic, Loader2, Plus, RefreshCw, Trash2, Edit3, Save, X, Volume2, Play, Pause, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";

const GENDERS = ["female", "male", "neutral"];
const STYLES = ["Professional", "Warm", "Energetic", "Calm"];
const STYLE_TINT = {
  Professional: "bg-sky-500/10 text-sky-300 border-sky-500/30",
  Warm: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  Energetic: "bg-rose-500/10 text-rose-300 border-rose-500/30",
  Calm: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
};

export default function AdminPersonas() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [playingId, setPlayingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/personas");
      setItems(data.items || []);
    } catch {
      toast.error("Could not load personas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (p.clients_using > 0) {
      toast.error("Disable instead — clients are using this voice");
      return;
    }
    if (!window.confirm(`Delete "${p.suggested_name}" persona permanently?`)) return;
    setBusyId(p.id);
    try {
      await api.delete(`/admin/personas/${p.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  const regen = async (p) => {
    setBusyId(p.id);
    try {
      const { data } = await api.post(`/admin/personas/${p.id}/regenerate-sample`);
      if (data.status === "generated") toast.success("Sample regenerated");
      else if (data.status === "skipped" && data.reason === "voice_provider_not_configured") {
        toast.error("Voice provider API key missing — configure it in backend/.env and restart");
      } else {
        toast.message(`Sample status: ${data.status}`);
      }
      load();
    } catch (err) {
      toast.error("Could not regenerate");
    } finally {
      setBusyId(null);
    }
  };

  const playSample = async (p) => {
    if (!p.sample_ready) {
      toast.message("Sample not ready — regenerate it first");
      return;
    }
    if (playingId === p.id) {
      window._raAudio?.pause();
      setPlayingId(null);
      return;
    }
    window._raAudio?.pause();
    try {
      const token = localStorage.getItem("ra_token");
      const url = `${API}/portal/personas/${p.id}/sample.mp3`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      // 403 expected for admin token — but admin can also fetch via dedicated route. We'll fall back.
      if (res.status === 403) {
        toast.message("Admin can only preview via client portal — coming soon");
        return;
      }
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const audio = new Audio(URL.createObjectURL(blob));
      audio.onended = () => setPlayingId(null);
      window._raAudio = audio;
      setPlayingId(p.id);
      audio.play();
    } catch {
      toast.error("Couldn't play");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-personas-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#FFB000] font-medium mb-2">Library</div>
          <h1 className="text-3xl font-bold tracking-tight">AI agent voices</h1>
          <p className="text-sm text-white/55 mt-2 max-w-2xl">The voice library every client picks from. Add new voices, disable old ones, and regenerate samples when you change a voice. Internal voice IDs are never shown in the client portal.</p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          data-testid="new-persona-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-black bg-[#FFB000] hover:bg-[#FFC633] transition-colors shadow-lg shadow-[#FFB000]/20"
        >
          <Plus className="w-4 h-4" /> New voice
        </button>
      </div>

      {showNew && <PersonaEditor onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}

      {loading ? (
        <div className="text-white/50 py-12 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-[#121214] border border-white/5 rounded-2xl p-12 text-center">
          <Mic className="w-10 h-10 text-white/20 mx-auto mb-4" strokeWidth={1.5} />
          <div className="text-white/70 font-medium">No personas yet</div>
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((p) => (
            <div key={p.id} className={`bg-[#121214] border rounded-2xl p-5 ${p.enabled ? "border-white/5" : "border-white/5 opacity-50"}`} data-testid={`admin-persona-${p.id}`}>
              {editingId === p.id ? (
                <PersonaEditor existing={p} onClose={() => setEditingId(null)} onSaved={() => { setEditingId(null); load(); }} />
              ) : (
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="w-11 h-11 rounded-xl bg-[#FFB000]/10 border border-[#FFB000]/20 flex items-center justify-center flex-shrink-0">
                    <Mic className="w-5 h-5 text-[#FFB000]" />
                  </div>
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-base font-semibold text-white">{p.suggested_name}</span>
                      <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded border font-medium ${STYLE_TINT[p.style] || ""}`}>
                        {p.style}
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.1em] text-white/40 capitalize">{p.gender}</span>
                      {!p.enabled && <span className="text-[10px] uppercase tracking-[0.1em] text-rose-400">disabled</span>}
                      {!p.sample_ready && <span className="text-[10px] uppercase tracking-[0.1em] text-amber-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> sample missing</span>}
                    </div>
                    <div className="text-[13px] text-white/65 mt-1.5">"{p.description}"</div>
                    <div className="text-[11px] font-mono text-white/35 mt-2">voice_id: {p.voice_id}</div>
                    <div className="text-[11px] text-white/40 mt-1">{p.clients_using} client{p.clients_using === 1 ? "" : "s"} using</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => playSample(p)}
                      disabled={!p.sample_ready}
                      data-testid={`play-${p.id}`}
                      className="text-xs px-3 py-1.5 rounded-md border border-white/10 text-white/70 hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1.5"
                    >
                      {playingId === p.id ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                      {playingId === p.id ? "Pause" : "Play"}
                    </button>
                    <button
                      onClick={() => regen(p)}
                      disabled={busyId === p.id}
                      data-testid={`regen-${p.id}`}
                      className="text-xs px-3 py-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 disabled:opacity-50 flex items-center gap-1.5"
                    >
                      {busyId === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                      Regen sample
                    </button>
                    <button
                      onClick={() => setEditingId(p.id)}
                      data-testid={`edit-${p.id}`}
                      className="text-xs px-3 py-1.5 rounded-md border border-sky-500/30 bg-sky-500/10 text-sky-300 hover:bg-sky-500/20 flex items-center gap-1.5"
                    >
                      <Edit3 className="w-3 h-3" /> Edit
                    </button>
                    <button
                      onClick={() => remove(p)}
                      disabled={p.clients_using > 0 || busyId === p.id}
                      data-testid={`delete-${p.id}`}
                      title={p.clients_using > 0 ? "Disable instead — clients use this" : "Delete"}
                      className="text-xs px-3 py-1.5 rounded-md border border-rose-500/30 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 disabled:opacity-30 flex items-center gap-1.5"
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PersonaEditor({ existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const [form, setForm] = useState({
    suggested_name: existing?.suggested_name || "",
    gender: existing?.gender || "female",
    style: existing?.style || "Professional",
    description: existing?.description || "",
    voice_id: existing?.voice_id || "",
    sample_text: existing?.sample_text || "",
    enabled: existing?.enabled ?? true,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.suggested_name || !form.description || !form.voice_id) {
      toast.error("Suggested name, description, and voice ID are required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/admin/personas/${existing.id}`, form);
        toast.success("Updated");
      } else {
        await api.post("/admin/personas", form);
        toast.success("Persona added");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-black/30 border border-white/10 rounded-xl p-5 space-y-4" data-testid={isEdit ? "edit-persona-form" : "new-persona-form"}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Suggested name" required>
          <input className="inp" value={form.suggested_name} onChange={(e) => set("suggested_name", e.target.value)} placeholder="e.g. Ashley" autoFocus data-testid="persona-name-input" />
        </Field>
        <Field label="Voice provider ID" required hint="Internal — never shown to clients">
          <input className="inp font-mono text-xs" value={form.voice_id} onChange={(e) => set("voice_id", e.target.value)} placeholder="e.g. 21m00Tcm4Tlvy3LB7Lo9" data-testid="persona-voice-id-input" />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Gender" required>
          <select className="inp" value={form.gender} onChange={(e) => set("gender", e.target.value)} data-testid="persona-gender-select">
            {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </Field>
        <Field label="Style" required>
          <select className="inp" value={form.style} onChange={(e) => set("style", e.target.value)} data-testid="persona-style-select">
            {STYLES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Description (visible to clients)" required hint="Short, neutral. e.g. 'Confident and clear'">
        <input className="inp" value={form.description} onChange={(e) => set("description", e.target.value)} maxLength={200} data-testid="persona-description-input" />
      </Field>
      <Field label="Sample text (optional)" hint="Used to generate the audio preview. Defaults to a generic greeting.">
        <textarea rows={2} className="inp" value={form.sample_text} onChange={(e) => set("sample_text", e.target.value)} placeholder='"Hi there, thanks for calling. How can I help you today?"' />
      </Field>
      {isEdit && (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} />
          <span className="text-white/70">Enabled (show in client portal)</span>
        </label>
      )}

      <div className="flex items-center gap-2 pt-2">
        <button type="submit" disabled={saving} data-testid="save-persona-btn" className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-black bg-[#FFB000] hover:bg-[#FFC633] disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving…" : (isEdit ? "Save changes" : "Add voice")}
        </button>
        <button type="button" onClick={onClose} className="text-sm text-white/55 hover:text-white px-3 py-2 flex items-center gap-1">
          <X className="w-4 h-4" /> Cancel
        </button>
      </div>

      <style>{`
        .inp { width: 100%; padding: 9px 12px; background: rgba(0,0,0,0.4); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; color: #fff; font-size: 13.5px; outline: none; transition: border-color .15s; }
        .inp:focus { border-color: #FFB000; }
        .inp::placeholder { color: rgba(255,255,255,0.3); }
      `}</style>
    </form>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-medium text-white/65 mb-1.5">{label} {required && <span className="text-[#FFB000]">*</span>}</div>
      {children}
      {hint && <div className="text-[11px] text-white/40 mt-1">{hint}</div>}
    </label>
  );
}
